import {
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionState,
  useDataChannel,
  useIsSpeaking,
  useLocalParticipant,
  useRemoteParticipants
} from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { CheckCircle, Loader2, Mic, MicOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { InterviewApiError, connectVoiceInterview } from '~/lib/interview-api';

interface VoiceInterfaceProps {
  token: string;
  language: string;
  jobTitle: string;
  employer: string | null;
  candidateName: string;
  onCompleted: () => void;
}

export function VoiceInterface({
  token,
  language,
  jobTitle,
  employer,
  candidateName,
  onCompleted
}: VoiceInterfaceProps): React.ReactNode {
  const { t } = useTranslation();
  const [connectionInfo, setConnectionInfo] = useState<{
    participantToken: string;
    livekitUrl: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(true);

  useEffect(() => {
    async function connect(): Promise<void> {
      try {
        const info = await connectVoiceInterview(token, language);
        setConnectionInfo(info);
        setConnecting(false);
      } catch (err) {
        // 409 means the session was already claimed by the other strict-mode mount — ignore
        if (err instanceof InterviewApiError && err.status === 409) return;
        setError(err instanceof Error ? err.message : 'Failed to connect');
        setConnecting(false);
      }
    }

    void connect();
  }, [token, language]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm text-center">
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (connecting || !connectionInfo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('screening.voice.connecting')}</p>
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      token={connectionInfo.participantToken}
      serverUrl={connectionInfo.livekitUrl}
      connect={true}
      audio={true}
      video={false}
    >
      <VoiceSession
        jobTitle={jobTitle}
        employer={employer}
        candidateName={candidateName}
        onCompleted={onCompleted}
      />
      <RoomAudioRenderer />
    </LiveKitRoom>
  );
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function ParticipantAvatar({
  name,
  initials,
  isSpeaking,
  variant
}: {
  name: string;
  initials: string;
  isSpeaking: boolean;
  variant: 'ai' | 'candidate';
}): React.ReactNode {
  const bgColor = variant === 'ai' ? 'bg-primary/10' : 'bg-muted';
  const textColor = variant === 'ai' ? 'text-primary' : 'text-foreground';
  const ringColor = variant === 'ai' ? 'ring-primary/50' : 'ring-foreground/30';

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        {/* Speaking ring animation */}
        {isSpeaking && (
          <div
            className={`absolute -inset-2 animate-pulse rounded-full ring-[3px] ${ringColor} ring-offset-2 ring-offset-background`}
          />
        )}
        <div
          className={`flex size-20 items-center justify-center rounded-full ${bgColor} ${textColor} text-xl font-semibold`}
        >
          {initials}
        </div>
      </div>
      <span className="text-sm text-muted-foreground">{name}</span>
    </div>
  );
}

function VoiceSession({
  jobTitle,
  employer,
  candidateName,
  onCompleted
}: {
  jobTitle: string;
  employer: string | null;
  candidateName: string;
  onCompleted: () => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const connectionState = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const [isMuted, setIsMuted] = useState(false);
  const [interviewFinished, setInterviewFinished] = useState(false);
  const [wasConnected, setWasConnected] = useState(false);
  const isConnected = connectionState === ConnectionState.Connected;

  const agentParticipant = remoteParticipants[0] ?? localParticipant;
  const localIsSpeaking = useIsSpeaking(localParticipant);
  const agentIsSpeaking = useIsSpeaking(agentParticipant);
  const hasAgent = remoteParticipants.length > 0;

  const candidateInitials = useMemo(() => getInitials(candidateName), [candidateName]);

  // Listen for completion data message from agent
  useDataChannel('interview-status', (msg) => {
    try {
      const data: unknown = JSON.parse(new TextDecoder().decode(msg.payload));
      if ((data as { type: string }).type === 'interview_complete') {
        setInterviewFinished(true);
      }
    } catch {
      // ignore malformed data
    }
  });

  // Track that we were connected at least once
  useEffect(() => {
    if (isConnected) setWasConnected(true);
  }, [isConnected]);

  // Fallback: if the room disconnects after being connected, show submit button
  useEffect(() => {
    if (wasConnected && connectionState === ConnectionState.Disconnected) {
      setInterviewFinished(true);
    }
  }, [wasConnected, connectionState]);

  const toggleMute = useCallback(() => {
    if (isMuted) {
      void localParticipant.setMicrophoneEnabled(true);
    } else {
      void localParticipant.setMicrophoneEnabled(false);
    }
    setIsMuted(!isMuted);
  }, [localParticipant, isMuted]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <div className="mx-auto max-w-lg">
          <p className="text-sm font-medium">{t('screening.voice.title')}</p>
          <p className="text-xs text-muted-foreground">
            {jobTitle}
            {employer ? ` — ${employer}` : ''}
          </p>
        </div>
      </div>

      {/* Main area */}
      <div className="flex flex-1 flex-col items-center justify-center px-4">
        {!isConnected && !interviewFinished && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('screening.voice.connecting')}
          </div>
        )}

        {isConnected && !interviewFinished && (
          <div className="flex w-full max-w-md flex-col items-center gap-8">
            {/* Participants — horizontal on desktop, vertical on mobile */}
            <div className="flex w-full flex-col items-center gap-10 sm:flex-row sm:justify-center sm:gap-16">
              <ParticipantAvatar
                name="Sara"
                initials="SA"
                isSpeaking={hasAgent && agentIsSpeaking}
                variant="ai"
              />
              <ParticipantAvatar
                name={candidateName}
                initials={candidateInitials}
                isSpeaking={localIsSpeaking}
                variant="candidate"
              />
            </div>

            <p className="text-sm text-muted-foreground">{t('screening.voice.inProgress')}</p>

            {/* Controls */}
            <div className="flex items-center gap-4">
              <Button
                variant={isMuted ? 'destructive' : 'outline'}
                size="icon"
                className="size-12 rounded-full"
                onClick={toggleMute}
              >
                {isMuted ? <MicOff className="size-5" /> : <Mic className="size-5" />}
              </Button>
            </div>

            {isMuted && <p className="text-xs text-destructive">{t('screening.voice.muted')}</p>}
          </div>
        )}

        {interviewFinished && (
          <div className="flex flex-col items-center gap-6">
            <div className="flex size-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="size-8 text-green-600" />
            </div>
            <p className="text-sm text-muted-foreground">{t('screening.voice.finished')}</p>
            <Button className="h-12 w-full max-w-xs" onClick={onCompleted}>
              <CheckCircle className="size-4" />
              {t('screening.submitInterview')}
            </Button>
          </div>
        )}
      </div>

      {/* AI disclosure footer */}
      <div className="border-t border-border px-4 py-3">
        <p className="text-center text-xs text-muted-foreground">{t('screening.aiDisclosure')}</p>
      </div>
    </div>
  );
}
